/**
 * Importi, date e percentuali del programma partner nella lingua di chi guarda: le funzioni del gestionale
 * (`core/i18n/formattazione.ts`), mai `toLocaleString` scritto a mano.
 *
 * Gli importi arrivano in CENTESIMI con la loro valuta; le date sono di calendario (`AAAA-MM-GG`, UTC: vengono da
 * Stripe), quindi si formattano in UTC e nessun fuso le può far scivolare al giorno prima.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { creaContestoLocale } from '@gestionale/core/i18n/lingue';
import { formatData, formatMese, formatNumero, formatPercentuale, formatValuta, parseNumero } from '@gestionale/core/i18n/formattazione';

export interface Formati {
  soldi: (centesimi: number, valuta?: string) => string;
  giorno: (data: string | null | undefined) => string;
  mese: (chiave: string) => string;
  percentuale: (valore: number) => string;
  /** Un numero da mettere in un campo modificabile: col separatore decimale di chi guarda, senza migliaia. */
  numeroPerCampo: (valore: number) => string;
  /** Il numero scritto in un campo (`7,5` o `7.5`, secondo la lingua); `NaN` se non è un numero. */
  leggiNumero: (testo: string) => number;
}

export function useFormati(): Formati {
  const { i18n } = useTranslation('partner');
  return useMemo(() => {
    const ctx = creaContestoLocale({ lingua: i18n.language, paese: 'IT', valuta: 'EUR', fusoOrario: 'Europe/Rome' });
    const calendario = creaContestoLocale({ lingua: i18n.language, paese: 'IT', valuta: 'EUR', fusoOrario: 'UTC' });
    return {
      soldi: (centesimi, valuta = 'EUR') => formatValuta(centesimi / 100, ctx, { valuta }),
      giorno: (data) => (data ? formatData(`${data}T00:00:00Z`, calendario, 'media') : ''),
      mese: (chiave) => formatMese(chiave, ctx),
      percentuale: (valore) => formatPercentuale(valore, ctx),
      numeroPerCampo: (valore) => (Number.isFinite(valore) ? formatNumero(valore, ctx, { raggruppa: false, decimaliMax: 4 }) : ''),
      leggiNumero: (testo) => parseNumero(testo, ctx) ?? NaN,
    };
  }, [i18n.language]);
}

/** Oggi come data di calendario, per i campi data dei dialoghi. */
export function oggi(): string {
  return new Date().toISOString().slice(0, 10);
}
